/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Dropdown from '../Shared/Dropdown';
import { fetchspecializations, fetchDistricts, addDoctor } from '../../Services/apiService';
import stripWhitespace from './StripWhitespace';
import '../../Styles/Form/addDoctor.css';

const AddDoctorForm = () => {
  const categories = [
    { id: 1, name: 'Diagnostic' },
    { id: 2, name: 'Clinic' },
    { id: 3, name: 'Hospital' },
    { id: 4, name: 'Private Chamber' },
  ];

  const [specializations, setSpecializations] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [orderPrefix, setOrderPrefix] = useState('');

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm();

  const selectedSpecializationId = watch('specialization_id');

  useEffect(() => {
    const fetchData = async () => {
      const specializationData = await fetchspecializations();
      setSpecializations(specializationData);
      const districtData = await fetchDistricts();
      setDistricts(districtData);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedSpecializationId) {
      const selectedSpecialization = specializations.find(
        (specialization) => specialization.id === parseInt(selectedSpecializationId, 10),
      );

      if (selectedSpecialization) {
        const prefix = String(selectedSpecialization.id).padStart(2, '0'); // Example: '01', '02'
        setOrderPrefix(prefix);
        const currentOrder = watch('order') || ''; // Preserve existing value
        setValue('order', `${prefix}${currentOrder.slice(2)}`);
      }
    }
  }, [selectedSpecializationId, specializations, watch, setValue]);

  const onSubmit = async (data) => {
    // Clean and trim the order value, remove leading zeros before submitting
    const formattedOrder = data.order.replace(/^0+/, ''); // Trim leading zeros

    const cleanObject = (obj) => {
      if (typeof obj !== 'object' || obj === null) {
        return typeof obj === 'string' ? stripWhitespace(obj) : obj;
      }
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = cleanObject(obj[key]);
        return acc;
      }, {});
    };

    const cleanedData = cleanObject(data);

    const formattedData = {
      doctor: {
        ...cleanedData,
        order: formattedOrder, // Use the cleaned order here
        doctor_specializations_attributes: [{ specialization_id: cleanedData.specialization_id }],
        doctor_schedules_attributes: [
          {
            ...cleanedData.doctor_schedules_attributes,
            chamber_attributes: cleanedData.chamber_attributes,
          },
        ],
      },
    };

    const response = await addDoctor(formattedData); // Assuming this makes the API request
    if (response) {
      alert('Doctor added successfully!');
      reset();
    } else {
      alert('Failed to add doctor. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h2>Add Doctor</h2>

      <Controller
        name="chamber_attributes.district_id"
        control={control}
        rules={{ required: 'District is required' }}
        render={({ field }) => (
          <Dropdown {...field} options={districts} label="Select District" />
        )}
      />
      {errors.chamber_attributes?.district_id && (
        <p className="error">{errors.chamber_attributes.district_id.message}</p>
      )}

      <Controller
        name="specialization_id"
        control={control}
        rules={{ required: 'Specialization is required' }}
        render={({ field }) => (
          <Dropdown {...field} options={specializations} label="Select Specialization" />
        )}
      />
      {errors.specialization_id && <p className="error">{errors.specialization_id.message}</p>}

      <label htmlFor="chamber_attributes.category">Chamber Category</label>
      <Controller
        name="chamber_attributes.category"
        control={control}
        rules={{ required: 'Chamber category is required' }}
        defaultValue="" // Ensure default value is an empty string
        render={({ field }) => (
          <select {...field}>
            <option value="" disabled>Select an option</option>
            {categories.map((category) => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        )}
      />

      {errors.chamber_attributes?.category && (
        <p className="error">{errors.chamber_attributes.category.message}</p>
      )}

      <label htmlFor="bangla-name">Bangla Name</label>
      <input
        type="text"
        id="bangla-name"
        {...register('bangla_name', { required: 'Bangla Name is required', maxLength: 100 })}
      />
      {errors['bangla-name'] && <p className="error">{errors['bangla-name'].message}</p>}

      <label htmlFor="name">English Name</label>
      <input
        type="text"
        id="name"
        {...register('name', { required: 'Name is required', maxLength: 100 })}
      />
      {errors.name && <p className="error">{errors.name.message}</p>}

      <label htmlFor="photo-url">Photo URL</label>
      <input
        type="text"
        id="photo-url"
        {...register('photo_url', { maxLength: 200 })}
      />
      {errors['photo-url'] && <p className="error">{errors['photo-url'].message}</p>}

      <label htmlFor="order">Order</label>
      <input
        type="number"
        id="order"
        {...register('order', {
          required: 'Order is required',
          min: 1, // Ensure the value is at least 1 and not negative
          max: 9999999, // Ensure the value doesn't exceed 7 digits
          validate: (value) => {
            const valueStr = String(value).padStart(7, '0'); // Add leading zeros for validation purposes
            // Ensure value has 7 digits, considering the order prefix
            return valueStr.length === 7 || 'Order must be 7 digits';
          },
        })}
        value={watch('order')}
        onChange={(e) => {
          const newValue = e.target.value;
          // Manually ensure the value has the prefix and 7 digits only
          setValue('order', `${orderPrefix}${newValue.slice(orderPrefix.length)}`);
        }}
      />
      {errors.order && <p className="error">{errors.order.message}</p>}

      <label htmlFor="specialty">Specialty</label>
      <input
        type="text"
        id="specialty"
        {...register('specialty', { required: 'Specialty is required' })}
      />
      {errors.specialty && <p className="error">{errors.specialty.message}</p>}

      <label htmlFor="special_notes">Special Notes</label>
      <input
        type="text"
        id="special-notes"
        {...register('special_notes', { maxLength: 500 })}
      />
      {errors.special_notes && <p className="error">{errors.special_notes.message}</p>}
      <label htmlFor="description">Description</label>
      <input
        type="text"
        id="description"
        {...register('description', { maxLength: 1000 })}
      />
      {errors.description && <p className="error">{errors.description.message}</p>}

      <label htmlFor="qualification">Qualification</label>
      <input
        type="text"
        id="qualification"
        {...register('qualification', { required: 'Qualification is required' })}
      />
      {errors.qualification && <p className="error">{errors.qualification.message}</p>}

      <label htmlFor="experience">Experience</label>
      <input
        type="text"
        id="experience"
        {...register('experience', { required: 'Experience is required' })}
      />
      {errors.experience && <p className="error">{errors.experience.message}</p>}

      <label htmlFor="doctor_schedules_attributes.available_day">Available Day</label>
      <input
        type="text"
        id="doctor_schedules_attributes.available_day"
        {...register('doctor_schedules_attributes.available_day', {
          required: 'Available day is required',
        })}
      />
      {errors.doctor_schedules_attributes?.available_day && (
        <p className="error">{errors.doctor_schedules_attributes.available_day.message}</p>
      )}

      <label htmlFor="doctor_schedules_attributes.available_time">Available Time</label>
      <input
        type="text"
        id="doctor_schedules_attributes.available_time"
        {...register('doctor_schedules_attributes.available_time', {
          required: 'Available time is required',
        })}
      />
      {errors.doctor_schedules_attributes?.available_time && (
        <p className="error">{errors.doctor_schedules_attributes.available_time.message}</p>
      )}

      <label htmlFor="doctor_schedules_attributes.contact">Contact</label>
      <input
        type="text"
        id="doctor_schedules_attributes.contact"
        {...register('doctor_schedules_attributes.contact', {
          required: 'Contact is required',
        })}
      />
      {errors.doctor_schedules_attributes?.contact && (
        <p className="error">{errors.doctor_schedules_attributes.contact.message}</p>
      )}

      <label htmlFor="chamber_attributes.name">Chamber Name</label>
      <input
        type="text"
        id="chamber_attributes.name"
        {...register('chamber_attributes.name', { required: 'Chamber name is required' })}
      />
      {errors.chamber_attributes?.name && (
        <p className="error">{errors.chamber_attributes.name.message}</p>
      )}

      <label htmlFor="chamber_attributes.address">Address</label>
      <input
        type="text"
        id="chamber_attributes.address"
        {...register('chamber_attributes.address', { required: 'Address is required' })}
      />
      {errors.chamber_attributes?.address && (
        <p className="error">{errors.chamber_attributes.address.message}</p>
      )}

      <button type="submit">Add Doctor</button>
    </form>
  );
};

export default AddDoctorForm;
